import { observer, useLocalObservable } from 'mobx-react';
import { useRef, useCallback } from 'react';
import AgentSidebar from '../sidebar/AgentSidebar';
import AgentSearchProvider from '../search/AgentSearchContext';
import AgentSearchModal from '../search/AgentSearchModal';
import AgentUserMessageInput from '../input/AgentUserMessageInput';
import useFooterVisibility from '../../../hooks/useFooterVisibility';
import { useStore } from '../../../store/store';
import { AI_DISCLAIMER_TEXT } from '../../../utils/constants/agent';

const AgentLayout = observer(
  ({
    children,
    // Sidebar props
    onNewChat = () => {},
    onSearchConversations = () => {},
    onBrowseByProject = () => {},
    onFavorites = () => {},
    onArchived = () => {},
    onShowMoreRecentChats = () => {},
    onChatClick = () => {},
    onRenameChat = () => {},
    onToggleChatFavorite = () => {},
    onArchiveChat = () => {},
    onDeleteChat = () => {},
    recentChats = [],
    isLoadingRecentChats = false,
    activeRoute = '',
    // Input area props
    showChatInput = false,
    onChatSubmit = () => {},
    onChatUpload = () => {},
    onChatStop = () => {},
    chatRunning = false,
    chatPlaceholder = 'Type your message...',
    chatInputDisabled = false,
    chatInputAutoFocus = false,
    // TD-2293: slot rendered inside the input area, above the message input —
    // used for the enrichment credit chip and the dynamic-options bar.
    inputTopSlot = null
  }) => {
    const { utilsStore } = useStore();
    const layoutRef = useRef(null); // TD-1730: Ref for setting CSS variable
    const previousHeightRef = useRef(null); // TD-1842: Track previous height for scroll compensation

    const layoutState = useLocalObservable(() => ({
      isSidebarCollapsed: false,
      setSidebarCollapsed: (collapsed) => {
        layoutState.isSidebarCollapsed = collapsed;
      }
    }));

    // TD-1730: Handle input height changes to update layout CSS variable
    // TD-1842: Scroll window down when input grows to prevent content being covered
    const handleInputHeightChange = useCallback((height) => {
      if (layoutRef.current) {
        layoutRef.current.style.setProperty('--agent-input-height', `${height}px`);
      }

      // TD-1842: Compensate for input expansion by scrolling down
      // This keeps the visible content stable when the input grows
      if (previousHeightRef.current !== null) {
        const heightDiff = height - previousHeightRef.current;
        if (heightDiff > 0) {
          // Input grew - scroll down to compensate
          window.scrollBy(0, heightDiff);
        }
        // Don't scroll when input shrinks - let content naturally fill the space
      }
      previousHeightRef.current = height;
    }, []);

    // Hide footer for agent layout
    useFooterVisibility(false);

    const agentLayoutClasses = ['agent-layout'];
    if (layoutState.isSidebarCollapsed) {
      agentLayoutClasses.push('sidebar-collapsed');
    }
    if (utilsStore.isMobileHamburgerOpen) {
      agentLayoutClasses.push('hidden');
    }

    return (
      <AgentSearchProvider>
        <div className={agentLayoutClasses.join(' ')} ref={layoutRef}>
          <div className="agent-layout-sidebar">
            <AgentSidebar
              onNewChat={onNewChat}
              onSearchConversations={onSearchConversations}
              onBrowseByProject={onBrowseByProject}
              onFavorites={onFavorites}
              onArchived={onArchived}
              onShowMoreRecentChats={onShowMoreRecentChats}
              onChatClick={onChatClick}
              onRenameChat={onRenameChat}
              onToggleChatFavorite={onToggleChatFavorite}
              onArchiveChat={onArchiveChat}
              onDeleteChat={onDeleteChat}
              recentChats={recentChats}
              isLoadingRecentChats={isLoadingRecentChats}
              activeRoute={activeRoute}
              onCollapseChange={layoutState.setSidebarCollapsed}
            />
          </div>

          <main className="agent-layout-main">
            <div className={`agent-layout-content ${showChatInput ? 'with-fixed-input' : ''}`}>
              {children}
            </div>
          </main>

          {/* Fixed input area - positioned relative to layout container */}
          {showChatInput && (
            <div className="agent-layout-input-area">
              {inputTopSlot && <div className="agent-layout-input-top">{inputTopSlot}</div>}
              <AgentUserMessageInput
                onSubmit={onChatSubmit}
                onUpload={onChatUpload}
                onStop={onChatStop}
                running={chatRunning}
                onHeightChange={handleInputHeightChange}
                placeholder={chatPlaceholder}
                disabled={chatInputDisabled}
                autoFocus={chatInputAutoFocus}
              />
              {/* TD-1829: AI Disclaimer - shown below input on active chats.
                  TD-2325: <output> carries an implicit status role for screen
                  readers without the (Sonar-flagged) explicit role attribute. */}
              <output className="agent-ai-disclaimer" aria-label="AI disclaimer notice">
                {AI_DISCLAIMER_TEXT}
              </output>
            </div>
          )}

          {/* Search modal - renders at layout level for proper z-index stacking */}
          {/* TODO: This AgentSearchModal was replaced by dedicated /conversations page */}
          {/* Modal implementation still exists but is never opened (no openModal() triggers) */}
          {/* Consider removing if modal search UI is not needed in future */}
          <AgentSearchModal />
        </div>
      </AgentSearchProvider>
    );
  }
);

export default AgentLayout;
